<template>
  <div class="d-flex flex-column align-items-center w-100">

    <!-- HEADER -->
    <div
      class="row mb-3 align-items-center w-100"
      style="background-color:#fcfcfc; padding:20px; border-radius:12px"
    >
      <!-- Switch vue -->
      <div class="col-md-6 d-flex gap-2">
        <button
          class="btn btn-sm rounded-pill px-4"
          :class="viewMode === 'table' ? 'btn-warning' : 'btn-white border'"
          @click="viewMode = 'table'"
        >
          <i class="bi bi-table"></i>
        </button>
        <button
          class="btn btn-sm rounded-pill px-4"
          :class="viewMode === 'card' ? 'btn-warning' : 'btn-white border'"
          @click="viewMode = 'card'"
        >
          <i class="bi bi-card-text"></i>
        </button>
      </div>

      <!-- Résultats -->
      <div class="col-md-6 text-end">
        <span class="fw-semibold">
          {{ totalFiltered ?? totalAll }} résultats
        </span>
      </div>
    </div>

    <!-- ===================== -->
    <!-- ===== CARD VIEW ===== -->
    <!-- ===================== -->
    <div v-if="viewMode === 'card'" class="row g-3 w-100">
      <div
        class="col-xl-3 col-lg-4 col-md-6"
        v-for="item in items"
        :key="item.id"
      >
        <div
          class="h-100"
          style="background-color:#fcfcfc; padding:20px; border-radius:12px; position:relative"
        >
          <!-- Statut -->
          <span
            v-html="item.status"
            style="position:absolute; top:-12px; right:-12px"
          ></span>

          <div class="d-flex flex-column gap-2 text-center">
            <div class="fw-semibold">
              Bon n° {{ item.number }}
            </div>

            <div v-if="item.from">
              <strong>Offert par :</strong> {{ item.from }}
            </div>

            <div v-if="item.to">
              <strong>À :</strong> {{ item.to }}
            </div>

            <div class="d-flex justify-content-center gap-2">
              <span v-html="item.prestations_prices"></span>
              <span v-html="item.balance"></span>
            </div>

            <div class="small text-muted">
              Émis le : {{ item.send_date?.display || '-' }}
            </div>

            <div class="small text-muted">
              Expire le : {{ item.end_date?.display || '-' }}
            </div>

            <div
              class="mt-2 d-flex justify-content-center flex-wrap"
              v-html="item.actions"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== -->
    <!-- ===== TABLE VIEW ==== -->
    <!-- ===================== -->
    <div
      v-if="viewMode === 'table'"
      class="w-100"
      style="background-color:#fcfcfc; padding:20px; border-radius:12px"
    >
      <div class="table-responsive">
        <table class="table table-bordered table-striped table-vcenter last-col-wide">
          <thead>
            <tr>
              <th
                v-for="col in columns"
                :key="col.data"
                class="text-center"
                style="font-size: 18px"
              >
                {{ col.title }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td
                v-for="col in columns"
                :key="col.data"
                class="text-center"
              >
                <span
                  v-if="col.data === 'send_date' || col.data === 'end_date'"
                >
                  {{ item[col.data]?.display }}
                </span>
                <span v-else v-html="item[col.data]"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- PAGINATION -->
    <div class="d-flex justify-content-between align-items-center mt-3 w-100">
      <span>
        {{ totalFiltered }} / {{ totalAll }} résultats
      </span>

      <nav>
        <ul class="pagination pagination-sm mb-0">

          <li class="page-item" :class="{ disabled: page === 1 || loading }">
            <button class="btn btn-sm btn-light" @click="page--" :disabled="page === 1 || loading">
              Précédent
            </button>
          </li>

          <li
            v-for="p in visiblePages"
            :key="p"
            class="page-item"
            :class="{ active: p === page }"
          >
            <button class="page-link" @click="page = p">
              {{ p }}
            </button>
          </li>

          <li class="page-item" :class="{ disabled: page === totalPages || loading }">
            <button class="btn btn-sm btn-light" @click="page++" :disabled="page === totalPages || loading">
              Suivant
            </button>
          </li>

        </ul>
      </nav>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  props: {
    fetchUrl: String,
    columns: Array
  },

  data () {
    return {
      items: [],
      page: 1,
      perPage: 10,
      totalAll: 0,
      totalFiltered: null,
      loading: false,
      viewMode: 'table'
    }
  },

  watch: {
    page () {
      this.fetchData()
    },

    viewMode (val) {
      localStorage.setItem('voucherViewMode', val)
    }
  },

  mounted () {
    this.viewMode = localStorage.getItem('voucherViewMode') || 'card'
    this.fetchData()
  },

  methods: {
    async fetchData () {
      this.loading = true

      try {
        const params = {
          draw: Date.now(),
          start: (this.page - 1) * this.perPage,
          length: this.perPage
        }

        this.columns.forEach((col, index) => {
          params[`columns[${index}][data]`] = col.data
          params[`columns[${index}][name]`] = col.name || col.data
        })

        const response = await axios.get(this.fetchUrl, { params })
        console.log('response',response);
        this.items = response.data.data ?? []
        this.totalAll = Number(response.data.recordsTotal ?? 0)
        this.totalFiltered = Number(response.data.recordsFiltered ?? this.totalAll)
      } catch (e) {
        console.error('Erreur chargement', e)
      } finally {
        this.loading = false
      }
    }
  },

  computed: {
    totalPages () {
      const total = this.totalFiltered ?? this.totalAll
      return Math.max(1, Math.ceil(total / this.perPage))
    },

    visiblePages () {
      const range = 2
      const start = Math.max(1, this.page - range)
      const end = Math.min(this.totalPages, this.page + range)

      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    }
  }
}
</script>
